/**
 * Align horizontal-bar analytics charts so category (series-name) columns and
 * plot areas share one left gutter across every chart in an answer.
 *
 * Highcharts sizes each chart's label column from its own longest category, so
 * stacked "Top Issues / Locations / Sectors" cards otherwise start their bars
 * at different x positions. We:
 *   1. measure the longest category across the whole set,
 *   2. clamp to [MIN, MAX] (ellipsis beyond MAX),
 *   3. apply the same marginLeft + label width to every bar chart.
 */

import type { Options, XAxisOptions } from 'highcharts'

/** Matches the compact chat chart label face (see HighchartsChart BASE_OPTIONS). */
const LABEL_FONT = '12px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'

/** Floor so short-label charts still leave a readable name column. */
export const MIN_LABEL_COL_PX = 96
/** Cap so one long location name cannot steal the plot area. */
export const MAX_LABEL_COL_PX = 180
/** Gap between the label column edge and the first bar. */
export const LABEL_GUTTER_PX = 20

function isBarChart(options: Options): boolean {
  const type = options.chart?.type
  // Backend analytics emit explicit type:"bar"; treat missing type as non-bar.
  return type === 'bar'
}

function collectCategories(options: Options): string[] {
  const x = options.xAxis
  if (!x) return []
  const axes = Array.isArray(x) ? x : [x]
  const out: string[] = []
  for (const ax of axes) {
    const cats = (ax as XAxisOptions).categories
    if (!Array.isArray(cats)) continue
    for (const c of cats) out.push(String(c ?? ''))
  }
  return out
}

/** Best-effort text width; falls back to a char estimate when canvas is unavailable. */
export function measureLabelWidthPx(text: string): number {
  const fallback = Math.ceil(Math.max(String(text).length, 1) * 7.2)
  if (typeof document === 'undefined') return fallback
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return fallback
    ctx.font = LABEL_FONT
    const w = Math.ceil(ctx.measureText(text).width)
    return w > 0 ? w : fallback
  } catch {
    return fallback
  }
}

function withSharedLabelColumn(axis: Options['xAxis'], labelColPx: number): Options['xAxis'] {
  const apply = (ax: XAxisOptions): XAxisOptions => ({
    ...ax,
    labels: {
      ...(ax.labels || {}),
      align: 'right',
      reserveSpace: true,
      style: {
        ...((ax.labels && ax.labels.style) || {}),
        width: labelColPx, // Highcharts CSSObject.width is number (px)
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  })

  if (!axis) {
    return apply({})
  }
  if (Array.isArray(axis)) {
    return axis.map((ax) => apply(ax || {}))
  }
  return apply(axis)
}

/**
 * Return a new Options[] where every bar chart shares one label-column width.
 * Non-bar charts are passed through unchanged.
 */
export function alignBarChartLabelColumns(charts: Options[]): Options[] {
  if (!charts.length) return charts

  const barCharts = charts.filter(isBarChart)
  if (!barCharts.length) return charts

  let longest = 0
  for (const chart of barCharts) {
    for (const cat of collectCategories(chart)) {
      longest = Math.max(longest, measureLabelWidthPx(cat))
    }
  }

  const labelColPx = Math.min(MAX_LABEL_COL_PX, Math.max(MIN_LABEL_COL_PX, longest))
  const marginLeft = labelColPx + LABEL_GUTTER_PX

  return charts.map((chart) => {
    if (!isBarChart(chart)) return chart
    return {
      ...chart,
      chart: {
        ...(chart.chart || {}),
        marginLeft,
      },
      xAxis: withSharedLabelColumn(chart.xAxis, labelColPx),
    }
  })
}
